import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Search,
  SlidersHorizontal,
  FolderOpen,
  Share2,
  Check,
  Shield,
  Scale,
  HelpCircle,
} from 'lucide-react';
import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const features = [
  { icon: Search, text: 'Search official government sources' },
  { icon: SlidersHorizontal, text: 'Stack, timeline, and stance views' },
  { icon: FolderOpen, text: 'Save trails and export collections' },
  { icon: Share2, text: 'Share cards for social' },
];

const trustCards = [
  { icon: Shield, text: 'Canonical sources linked' },
  { icon: Scale, text: 'Show counter-evidence' },
  { icon: HelpCircle, text: 'Mark inconclusive when warranted' },
];

export function FeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const trustRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headlineRef.current,
        { y: '-6vh', opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            end: 'top 50%',
            scrub: true,
          },
        }
      );

      gsap.fromTo(
        featuresRef.current?.children || [],
        { x: '-10vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: featuresRef.current,
            start: 'top 80%',
            end: 'top 40%',
            scrub: true,
          },
        }
      );

      gsap.fromTo(
        cardRef.current,
        { x: '14vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: cardRef.current,
            start: 'top 80%',
            end: 'top 40%',
            scrub: true,
          },
        }
      );

      gsap.fromTo(
        trustRef.current?.children || [],
        { y: '8vh', opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: trustRef.current,
            start: 'top 85%',
            end: 'top 55%',
            scrub: true,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing z-[70] bg-[#F6F5F2] py-24"
      style={{ minHeight: '150vh' }}
    >
      {/* Headline Group */}
      <div ref={headlineRef} className="mb-16 px-[9vw]">
        <h2
          className="font-display font-bold leading-[1.05] tracking-[-0.02em] text-[#0B0D10]"
          style={{ fontSize: 'clamp(34px, 3.6vw, 52px)' }}
        >
          Built for speed. Designed for trust.
        </h2>
        <p
          className="mt-4 max-w-xl leading-relaxed text-[#6B6F78]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)' }}
        >
          Source search, timeline views, saved trails, and exports—without losing citation
          integrity.
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-2 gap-12 px-[9vw]">
        {/* Features List */}
        <div ref={featuresRef} className="space-y-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="receipt-card-sm flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#E1062C]">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <span className="font-medium text-[#0B0D10]">{feature.text}</span>
              </div>
            );
          })}
        </div>

        {/* Large Receipt Card */}
        <div ref={cardRef} className="receipt-card p-6" style={{ height: '56vh' }}>
          {/* Card Header */}
          <div className="mb-4 flex items-center gap-3 border-b border-[rgba(11,13,16,0.08)] pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E1062C]">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-[#0B0D10]">Your Evidence Pack</p>
              <p className="font-mono text-xs text-[#6B6F78]">3 sources • 3 lanes</p>
            </div>
          </div>

          {/* Card Content */}
          <div className="space-y-3">
            <div className="rounded-lg bg-[#F6F5F2] p-3">
              <div className="mb-2 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span className="font-mono text-xs text-[#6B6F78]">Lane A</span>
              </div>
              <p className="text-sm text-[#0B0D10]">Fair Labor Standards Act</p>
            </div>

            <div className="rounded-lg bg-[#F6F5F2] p-3">
              <div className="mb-2 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span className="font-mono text-xs text-[#6B6F78]">Lane B</span>
              </div>
              <p className="text-sm text-[#0B0D10]">Congressional Record, 1966</p>
            </div>

            <div className="rounded-lg bg-[#F6F5F2] p-3">
              <div className="mb-2 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span className="font-mono text-xs text-[#6B6F78]">Lane C</span>
              </div>
              <p className="text-sm text-[#0B0D10]">BLS Current Population Survey</p>
            </div>
          </div>

          {/* Card Footer */}
          <div className="mt-4 border-t border-[rgba(11,13,16,0.08)] pt-4">
            <Link
              to="/signup"
              className="block w-full rounded-xl bg-[#E1062C] py-3 text-center font-medium text-white transition-colors hover:bg-[#c00525]"
            >
              Build your evidence pack
            </Link>
          </div>
        </div>
      </div>

      {/* Trust Cards */}
      <div className="mt-16 px-[9vw]">
        <div ref={trustRef} className="flex gap-4">
          {trustCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div key={index} className="receipt-card-sm flex flex-1 items-center gap-3 p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#F6F5F2]">
                  <Icon className="h-5 w-5 text-[#E1062C]" />
                </div>
                <span className="font-medium text-[#0B0D10]">{card.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
